import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Button from '@pelckmans/elementis/components/button';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import InfoIcon from '@pelckmans/elementis/icons/info';
import PelckmansIcon from '@pelckmans/elementis/icons/pelckmans';
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GroupInviteState } from '../../../components/group-invite/GroupInvite';
import InvitationContext from '../../../contexts/InvitationContext';
import { useRoutes } from '../../../hooks/useRoutes';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import formatTeacherName from '../../../utils/formatTeacherName';
import { GroupInviteMessage } from '../../group-invite';
import { acceptGroupInvite, declineGroupInvite } from '../api/my-teachers-api';
import useStudentGroups from '../hooks/useStudentGroups';
import { toId } from '../utils';

export default function PendingList() {
  const { t } = useTranslation();
  const { currentSchoolYear } = useSchoolYear();
  const { pendingInvitations, handleInvitation } = useContext(InvitationContext);
  const { refetch } = useStudentGroups(toId(currentSchoolYear));
  const { buildAbsolutePath } = useRoutes();
  const handleError = useThrowAsyncError();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleAccept = useCallback(
    async (invitation: typeof pendingInvitations[number]) => {
      try {
        await acceptGroupInvite(invitation.id);
        handleInvitation(invitation.id);

        refetch(toId(currentSchoolYear));
        navigate('.', {
          state: {
            groupInviteModal: {
              state: GroupInviteState.Success,
              teacher: invitation.owner,
            },
          },
        });
      } catch (e) {
        if (e instanceof AxiosError) {
          let state, message;
          switch ((e as AxiosError).response?.status) {
            case 403:
              state = GroupInviteState.Failed;
              message = GroupInviteMessage.NonStudent;
              break;
            case 409:
              state = GroupInviteState.Failed;
              message = GroupInviteMessage.TooManyMembers;
              break;
            default:
              state = GroupInviteState.Failed;
              message = GroupInviteMessage.GeneralError;
          }

          if (state) {
            return navigate('.', {
              state: {
                groupInviteModal: {
                  state: GroupInviteState.Failed,
                  message,
                  teacher: invitation.owner,
                  retryUrl: `${buildAbsolutePath(['MY_TEACHERS'])}?acceptInvitationId=${invitation.id}`,
                },
              },
            });
          }

          handleError(e);
        }
      }
    },
    [refetch, handleInvitation, navigate, buildAbsolutePath, handleError, currentSchoolYear],
  );

  useEffect(() => {
    const acceptInvitationId = searchParams.get('acceptInvitationId');

    if (!acceptInvitationId) return;

    const invitation = pendingInvitations.find(invitation => invitation.id === acceptInvitationId);

    if (invitation) {
      handleAccept(invitation);
    }
  }, [pendingInvitations, searchParams, setSearchParams, handleAccept]);

  const handleDecline = async (invitationId: string) => {
    await declineGroupInvite(invitationId);
    handleInvitation(invitationId);
  };

  return (
    <List emptyMessage={t('PAGES.MY_TEACHERS.PENDING_LIST.EMPTY')}>
      <List.Header
        title={t('PAGES.MY_TEACHERS.PENDING_LIST.TITLE')}
        titleTag="h3"
        prominent
        tooltip={
          <Tooltip anchor={<InfoIcon data-testid="info-icon" />}>
            <p>{t('PAGES.MY_TEACHERS.PENDING_LIST.TOOLTIP')}</p>
          </Tooltip>
        }
      />
      {pendingInvitations
        .filter(invitation => invitation.schoolYear === toId(currentSchoolYear))
        .map(invitation => (
          <List.Item
            key={invitation.id}
            title={formatTeacherName(invitation.owner)}
            subText={invitation.name}
            data-testid={`teacher-${invitation.id}`}
            pillGroupProps={{
              role: 'group',
              'aria-label': t('PAGES.MY_TEACHERS.PENDING_LIST.PILL_GROUP_PROPS.A11Y_LABEL'),
              children: invitation.subjects.map((s, i) => <Pill key={`${i}-${s}`}>{s}</Pill>),
            }}
            actionArea={
              <>
                <Button prominence="danger" size="sm" onClick={() => handleDecline(invitation.id)}>
                  {t('PAGES.MY_TEACHERS.PENDING_LIST.ACTIONS.DECLINE')}
                </Button>
                <Button prominence="success" size="sm" onClick={() => handleAccept(invitation)}>
                  {t('PAGES.MY_TEACHERS.PENDING_LIST.ACTIONS.ACCEPT')}
                </Button>
              </>
            }
            icon={<PelckmansIcon />}
          />
        ))}
    </List>
  );
}
